import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ProcessTemplateParameterResource, TemplateParameterResource } from "@octopusdeploy/octopus-server-client";
import { AccountType, ControlType, isAccountControlType, Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useMemo } from "react";
import { defaultScopeValues } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateActionParameterValues";
import MultiLineTextInput from "~/components/ControlInputs/MultiLineTextInput";
import SelectInput from "~/components/ControlInputs/SelectInput";
import SingleLineTextInput from "~/components/ControlInputs/SingleLineTextInput";
import type { DoBusyTask } from "~/components/DataBaseComponent/DataBaseComponent";
import { IconButtonWithTooltip } from "~/components/IconButtonWithTooltip";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
import type { TemplateValueKey } from "../TemplateParametersTable";
import { PlatformHubAccountSelect, TemplateParameterScopeCell } from "../TemplateParametersTable";
const SELECT_OPTIONS_KEY = "Octopus.SelectOptions";
interface ParameterDefaultValueViewProps {
    model: ProcessTemplateParameterResource;
    setModel: (updates: Partial<ProcessTemplateParameterResource>) => void;
    controlType: ControlType;
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    isEditingScope: boolean;
    setIsEditingScope: (isEditing: boolean) => void;
    doBusyTask: DoBusyTask;
}
const getAccountTypesForControlType = (controlType: ControlType): AccountType[] => {
    switch (controlType) {
        case ControlType.AmazonWebServicesAccount:
            return [AccountType.AmazonWebServicesAccount, AccountType.AmazonWebServicesOidcAccount];
        case ControlType.AzureAccount:
            return [AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc];
        case ControlType.GoogleCloudAccount:
            return [AccountType.GoogleCloudAccount];
        case ControlType.UsernamePasswordAccount:
            return [AccountType.UsernamePassword];
        case ControlType.GenericOidcAccount:
            return [AccountType.GenericOidcAccount];
        default:
            return [];
    }
};
interface AccountValuesUIProps {
    model: ProcessTemplateParameterResource;
    setModel: (updates: Partial<ProcessTemplateParameterResource>) => void;
    controlType: ControlType;
    focusedValueKey: TemplateValueKey | null;
    setFocusedValueKey: (key: TemplateValueKey | null) => void;
    isEditingScope: boolean;
    setIsEditingScope: (isEditing: boolean) => void;
}
const AccountValuesUI: React.FC<AccountValuesUIProps> = (props) => {
    const { model, setModel, controlType, focusedValueKey, setFocusedValueKey, isEditingScope, setIsEditingScope } = props;
    const { result: environments } = useQuery((repository) => repository.PlatformHub.getEnvironmentSummaries(), [], "Get environment summaries");
    const { result: platformHubAccounts } = useQuery((repository) => repository.PlatformHubAccounts.get({ Take: Repository.takeAll }), [], "Get all platform hub accounts");
    const availableScopeValues = useMemo(() => {
        return { ...defaultScopeValues, Environments: environments?.map((e) => ({ Id: `${e.SpaceId}/${e.Id}`, Name: `${e.Name} (${e.SpaceName})` })) ?? [] };
    }, [environments]);
    const updateModel = (newValues: Partial<ProcessTemplateParameterResource>) => setModel(newValues);
    const addAccountValue = () => updateModel({ Values: [...model.Values, { Value: "", Scope: {} }] });
    const removeAccountValue = (index: number) => updateModel({ Values: model.Values.filter((_, i) => i !== index) });
    const updateAccountValue = (index: number, value: string) => updateModel({
        Values: model.Values.map((v, i) => (i === index ? { ...v, Value: value } : v)),
    });
    if (!platformHubAccounts?.Items || !environments) {
        return <div>Loading...</div>;
    }
    const accountTypes = getAccountTypesForControlType(controlType);
    return (<>
            {model.Values.length === 0 ? (<>
                    <div className={styles.emptyStateBox}>
                        <p className={styles.emptyStateText}>You have not added any parameter values yet</p>
                    </div>
                    <p className={styles.emptyStateDescription}>Account values are configured in Platform Hub. To add an account, go to "Accounts" in the Platform Hub.</p>
                </>) : (model.Values.map((value, index) => (<div key={index} className={styles.valueRowContainer}>
                        <div>
                            <PlatformHubAccountSelect onChange={(val: string | undefined) => updateAccountValue(index, val ?? "")} value={value.Value} accounts={platformHubAccounts.Items} type={accountTypes} placeholder="Default value"/>
                        </div>
                        <div>
                            <TemplateParameterScopeCell parameter={model} value={value} onChange={(newParameter: TemplateParameterResource) => updateModel({ Values: newParameter.Values })} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditing={isEditingScope} setIsEditing={setIsEditingScope} index={index} scopeValues={availableScopeValues} height={48} width={250} isConsumer={false}/>
                        </div>
                        <div>
                            <IconButtonWithTooltip toolTipContent="Remove" icon="XmarkIcon" accessibleName="Remove value" onClick={() => removeAccountValue(index)}/>
                        </div>
                    </div>)))}
            <Button label="Add Value" onClick={addAccountValue} importance="secondary"/>
        </>);
};
AccountValuesUI.displayName = "AccountValuesUI"
export const ParameterDefaultValueView: React.FC<ParameterDefaultValueViewProps> = (props) => {
    const { model, setModel, controlType, focusedValueKey, setFocusedValueKey, isEditingScope, setIsEditingScope, doBusyTask } = props;
    const updateModel = (newValues: Partial<ProcessTemplateParameterResource>) => setModel(newValues);
    const renderDefaultValueInput = () => {
        const defaultValue = model.Values.length > 0 ? model.Values[0].Value : "";
        const handleValueChange = (value: string | undefined) => updateModel({ Values: value ? [{ Value: value, Scope: {} }] : [] });
        const commonInputProps = {
            onChange: handleValueChange,
            value: defaultValue,
            localNames: [],
            warning: undefined,
            defaultValueIndicator: undefined,
        };
        switch (controlType) {
            case ControlType.SingleLineText:
                return <SingleLineTextInput label="Default Value (Optional)" {...commonInputProps} monoSpacedFont={true} autoFocus={false}/>;
            case ControlType.MultiLineText:
                return <MultiLineTextInput label="Default Value (Optional)" {...commonInputProps} monoSpacedFont={true} autoFocus={false}/>;
            case ControlType.Select:
                return <SelectInput label="Default Value (Optional)" {...commonInputProps} resetValue="" options={selectOptionsToItems(model.DisplaySettings[SELECT_OPTIONS_KEY])} allowClear={false} doBusyTask={doBusyTask}/>;
            case ControlType.Checkbox:
                return (<SelectInput label="Default Value (Optional)" {...commonInputProps} resetValue="" options={[
                        { value: "true", text: "True" },
                        { value: "false", text: "False" },
                    ]} allowClear={true} doBusyTask={doBusyTask}/>);
            default:
                return null;
        }
    };
    if (isAccountControlType(controlType)) {
        return (<div className={styles.container}>
                <p>To specify a default account parameter value, start by clicking on "Add Value".</p>
                <AccountValuesUI model={model} setModel={setModel} controlType={controlType} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} isEditingScope={isEditingScope} setIsEditingScope={setIsEditingScope}/>
            </div>);
    }
    return (<div className={styles.container}>
            <p>Specify a default value for this parameter.</p>
            {renderDefaultValueInput()}
        </div>);
};
ParameterDefaultValueView.displayName = "ParameterDefaultValueView"
const styles = {
    // Horizontal padding to prevent content from being cut off on the sides
    container: css({
        padding: space[4],
    }),
    emptyStateBox: css({
        padding: `${space[64]} ${space[24]}`,
        border: `1px dashed ${themeTokens.color.border.secondary}`,
        borderRadius: borderRadius.small,
        marginBottom: space[16],
        textAlign: "center",
    }),
    emptyStateText: css({
        margin: 0,
        fontSize: "16px",
        fontWeight: 500,
        color: themeTokens.color.text.secondary,
    }),
    emptyStateDescription: css({
        margin: 0,
        marginBottom: space[16],
        fontSize: "14px",
        color: themeTokens.color.text.secondary,
    }),
    valueRowContainer: css({
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
        gap: space[12],
        marginBottom: space[12],
        padding: space[16],
        border: `1px solid ${themeTokens.color.border.secondary}`,
        borderRadius: borderRadius.small,
        maxWidth: "100%",
    }),
};
